import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { DateTime } from 'luxon';
import { breakpoints, belowBreakpoint, betweenBreakpoints, getIsInProgress } from '../utils';
import styled, { css } from 'styled-components';
import _ from 'lodash';
import SessionHelper from '../helpers/SessionHelper';
import {
  getGuestBookButtonText,
  isDataSourceMemberBookable,
} from '../../shared/utils/guestBookButton';
import { ConfigContext } from '../ConfigContext';
import { deepLinkParamGetters } from '../../shared/utils/deepLinkUtils';
import { renderMustacheTemplateIfAllTemplateParametersArePresent } from '../../shared/utils/mustacheUtils';

/**
 * @typedef {{
 *  admissionModal: boolean;
 *  disabled: boolean;
 *  shouldBeGreyedOut: boolean;
 *  shouldDisplayNone: boolean;
 * }} BookButtonProps
 */

const BookButton =
  /** @type {import('styled-components').ThemedStyledFunction<'div', BookButtonProps>} */
  (styled.button)`
    min-width: 195px;
    display: inline-block;
    font-weight: 400;
    text-align: center;
    white-space: nowrap;
    vertical-align: middle;
    user-select: none;
    border: 1px solid transparent;
    padding: 0.375rem 0.75rem;
    font-size: 1rem;
    line-height: 1.5;
    border-radius: 0.25rem;
    cursor: pointer;
    margin-right: 0.5rem;
    background: ${(props) => props.theme.button};
    color: white;
    text-decoration: none;

    ${(/** @type {BookButtonProps} */ props) =>
      props.admissionModal &&
      css`
        margin-left: auto !important;
      `}

    ${(/** @type {BookButtonProps} */ props) =>
      props.disabled &&
      css`
        background: grey;
        cursor: default;
      `}

  ${(/** @type {BookButtonProps} */ props) =>
      props.shouldBeGreyedOut &&
      css`
        background: grey;
        cursor: default;
        pointer-events: none;
      `}

  ${(/** @type {BookButtonProps} */ props) =>
      props.shouldDisplayNone &&
      css`
        visibility: hidden;
      `}

  ${belowBreakpoint(breakpoints.md)} {
      font-size: 0.9rem;
      min-width: 125px;
      margin: 0;
      padding: 0.2rem 0.4rem;
      font-weight: 300;

      & ~ & {
        margin-left: 5px;
        margin-right: 5px;
      }

      ${(/** @type {BookButtonProps} */ props) =>
        props.shouldDisplayNone &&
        css`
          display: none;
        `}
    }
  `;

const BookButtonsWrapper =
  /** @type {import('styled-components').ThemedStyledFunction<'div', {isSessionPopover:boolean}>} */
  (styled.div)`
    display: flex;
    flex-direction: row;

    ${betweenBreakpoints(breakpoints.sm, breakpoints.md)} {
      justify-content: flex-start;
    }

    ${(props) =>
      props.isSessionPopover &&
      css`
        margin-bottom: 5px;
        width: max-content;
        margin-left: auto;
      `}
  `;

const BookButtons = ({
  subEvent,
  eventSeries,
  checkoutBaseUrl,
  checkoutTheme = undefined,
  bookableProviders: bookableDataSources,
  uniqueId,
  provider: dataSource,
  isSessionPopover = false,
  memberDeepLinks: memberDeepLinksV1,
  setModal = ({ isOpen, props }) => null,
  admissionsPolicyUrl,
}) => {
  const timetableConfig = useContext(ConfigContext);
  const { memberDeepLinksV2, showWaitingListBtn } = timetableConfig;
  const memberDeepLinksAllVersions = { ...memberDeepLinksV1, ...memberDeepLinksV2 };
  const isDataSourceGuestBookable = bookableDataSources.includes(dataSource);
  const doAnyDataSourcesShowGuestAndMemberButtons = (() => {
    if (_.isEmpty(memberDeepLinksAllVersions)) return false;
    if (_.isEmpty(bookableDataSources)) return false;

    return Object.keys(memberDeepLinksAllVersions).some((deepLinkDataSourceKey) =>
      bookableDataSources.includes(deepLinkDataSourceKey)
    );
  })();
  const doAnyDataSourcesShowGuestOrMemberButtons =
    _.isEmpty(memberDeepLinksAllVersions) && _.isEmpty(bookableDataSources) ? false : true;
  const date = DateTime.fromISO(subEvent.startDate);
  const dateString = `${date.year}-${pad2Digit(date.month)}-${pad2Digit(date.day)}`;
  const activityId = uniqueId.split('-')[2];

  const isFull = subEvent.remainingAttendeeCapacity === 0;
  const isInProgress = getIsInProgress(subEvent);

  const isAvailableToBook = subEvent['imin:checkoutUrlTemplate'] && isDataSourceGuestBookable;

  const isThisDataSourceMemberBookable = isDataSourceMemberBookable(
    memberDeepLinksAllVersions,
    dataSource
  );

  const nonMemberCheckoutButtonText = getGuestBookButtonText({
    isFull,
    isAvailableToBook,
    isThisDataSourceMemberBookable,
    guestButtonCustomText: timetableConfig.customText?.bookButtons?.guest,
  });

  function evaluateGuestBookButton() {
    // If the Data Source allows guest booking, return a BookButton that leads to Checkout
    if (isDataSourceGuestBookable) {
      // TODO this should render as a link – not a button – if not doing modal.
      // If doing modal, then the button within the modal should be a link.
      // Buttons cause irritating UI
      return (
        <BookButton
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();

            if (isAvailableToBook && admissionsPolicyUrl) {
              setModal({
                isOpen: true,
                props: {
                  url: SessionHelper.checkoutUrl(
                    checkoutBaseUrl,
                    subEvent['imin:checkoutUrlTemplate'],
                    checkoutTheme
                  ),
                  buttonText: 'Pay & Play Booking',
                  admissionsPolicyUrl,
                },
              });
            } else if (isAvailableToBook && !admissionsPolicyUrl) {
              window.open(
                SessionHelper.checkoutUrl(
                  checkoutBaseUrl,
                  subEvent['imin:checkoutUrlTemplate'],
                  checkoutTheme
                ),
                '_blank',
                'noopener,noreferrer'
              );
            }
          }}
          shouldDisplayNone={isInProgress || isFull}
          shouldBeGreyedOut={!isAvailableToBook}
        >
          {nonMemberCheckoutButtonText}
        </BookButton>
      );
    }
    // If the Data Source does not allow guest booking but does allow member booking this row will have one button
    // If there is a Data Source that does not have both guest and member booking, the other rows will only have 1 button
    // Therefore we do not want a guest button
    if (
      !isDataSourceGuestBookable &&
      isThisDataSourceMemberBookable &&
      !doAnyDataSourcesShowGuestAndMemberButtons
    ) {
      return null;
    }
    // If the Data Source does not allow guest booking but does allow member booking this row will have one button
    // If there is a Data Source that has both guest and member booking, the other rows will have 2 buttons
    // Therefore we want an invisible guest button for this row for alignment purposes
    if (
      !isDataSourceGuestBookable &&
      isThisDataSourceMemberBookable &&
      doAnyDataSourcesShowGuestAndMemberButtons
    ) {
      return <BookButton shouldDisplayNone={true}></BookButton>;
    }
    // If the Data Source does not allow guest booking or member booking but another Data Source in the table does allow either booking
    // return a BookButton that says to contact the centre
    if (
      !isDataSourceGuestBookable &&
      !isThisDataSourceMemberBookable &&
      doAnyDataSourcesShowGuestOrMemberButtons
    ) {
      return <BookButton shouldBeGreyedOut={true}>{nonMemberCheckoutButtonText}</BookButton>;
    }

    // If the Data Source does not allow guest booking, and neither it or any other Data Sources allow member booking, we want no buttons
    return null;
  }

  function evaluateDeepLinkBookButton() {
    // If this Data Source has member booking, return a button that takes the user to the member booking page
    if (isThisDataSourceMemberBookable) {
      const memberCheckoutButtonText = (() => {
        if (isFull) {
          return timetableConfig.customText?.bookButtons?.member?.ifFull ?? 'Waiting List';
        }
        // If isDataSourceGuestBookable is true, then it means that guests can also book, and therefore member and non-member
        // booking must be differentiated.
        // Note that this logic only pertains to the default text. If custom text is provided, it is assumed that the client
        // will choose something that is sufficient for their situation and may find the distinction confusing.
        if (isDataSourceGuestBookable) {
          return timetableConfig.customText?.bookButtons?.member?.ifAvailable ?? 'Member Booking';
        }
        // If it's just member booking that's possible, make it explicit that the user must log in before booking
        return timetableConfig.customText?.bookButtons?.member?.ifAvailable ?? 'Login and Book';
      })();
      const deepLinkUrl = (() => {
        if (memberDeepLinksV1[dataSource]) {
          const deepLinkBaseUrl = memberDeepLinksV1[dataSource];
          return `${deepLinkBaseUrl}Activities=${activityId}&StartDate=${dateString}`;
        }
        if (memberDeepLinksV2[dataSource]) {
          const deepLinkTemplate = memberDeepLinksV2[dataSource];
          const deepLinkParams = {
            ...deepLinkParamGetters.sessionSeriesRpdeId(eventSeries),
            ...deepLinkParamGetters.startDateIsoUtc(subEvent),
          };
          return renderMustacheTemplateIfAllTemplateParametersArePresent(
            deepLinkTemplate,
            deepLinkParams
          );
        }
        return undefined;
      })();
      if (deepLinkUrl) {
        if (isFull && showWaitingListBtn === false) {
          return doAnyDataSourcesShowGuestAndMemberButtons ? (
            <BookButton shouldDisplayNone={true}></BookButton>
          ) : null;
        }
        return (
          <BookButton
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              if (admissionsPolicyUrl) {
                setModal({
                  isOpen: true,
                  props: {
                    url: deepLinkUrl,
                    buttonText: memberCheckoutButtonText,
                    admissionsPolicyUrl,
                  },
                });
              } else {
                window.open(deepLinkUrl, '_blank', 'noopener,noreferrer');
              }
            }}
            shouldDisplayNone={isInProgress}
          >
            {memberCheckoutButtonText}
          </BookButton>
        );
      }
    }
    // If this Data Source does not have member booking but another Data Source does, render an invisible member button for
    // alignment purposes
    if (doAnyDataSourcesShowGuestAndMemberButtons) {
      return <BookButton shouldDisplayNone={true}></BookButton>;
    }
    // If no Data Sources have member booking, return no buttons
    return null;
  }

  if (isThisDataSourceMemberBookable) {
    return (
      <BookButtonsWrapper isSessionPopover={isSessionPopover}>
        {evaluateGuestBookButton()}
        {evaluateDeepLinkBookButton()}
      </BookButtonsWrapper>
    );
  }
  // If this Data Source does not have deep link booking, but another Data Source in the timetable does, render the guest button
  // second for alignment purposes
  return (
    <BookButtonsWrapper isSessionPopover={isSessionPopover}>
      {evaluateDeepLinkBookButton()}
      {evaluateGuestBookButton()}
    </BookButtonsWrapper>
  );
};

/**
 * ```
 * > pad2Digit(1)
 * '01'
 * > pad2Digit(12)
 * '12'
 * ```
 *
 * @param {number} num
 */
function pad2Digit(num) {
  return String(num).padStart(2, '0');
}

export { BookButton, BookButtons };

BookButtons.propTypes = {
  checkoutBaseUrl: PropTypes.string,
  checkoutTheme: PropTypes.string,
  subEvent: PropTypes.object,
  eventSeries: PropTypes.object,
  bookableProviders: PropTypes.array,
  uniqueId: PropTypes.string,
  provider: PropTypes.string,
  isSessionPopover: PropTypes.bool,
  memberDeepLinks: PropTypes.object,
  setModal: PropTypes.func,
  admissionsPolicyUrl: PropTypes.string,
};
